import { useEffect, useRef } from "react";
import confetti from "canvas-confetti";
import { useT } from "../../lib/i18n";
import checkIcon from "../../assets/player/circle-check.svg";
import "./lesson-completion.css";

/** A finite celebration belongs to this player, including when it is in fullscreen. */
export function LessonCompletion({ title, nextTitle, onReplay, onContinue, onDone }: {
  title: string; nextTitle?: string; onReplay: () => void; onContinue?: () => void; onDone: () => void;
}) {
  const t = useT();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particles = useRef<ReturnType<typeof confetti.create> | null>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (reduced.matches) return;
    // Reuse the same canvas instance when StrictMode replays this effect.
    const fire = particles.current ??= confetti.create(canvas, { resize: true, disableForReducedMotion: true });
    const colors = ["#409eff", "#88c2ff", "#72d5bb", "#ffd166", "#f4a9bf"];
    const options = { colors, ticks: 170, gravity: 0.85, decay: 0.92, scalar: 0.85, disableForReducedMotion: true };
    void fire({ ...options, particleCount: 48, angle: 62, spread: 48, startVelocity: 37, origin: { x: 0.08, y: 0.75 } });
    void fire({ ...options, particleCount: 48, angle: 118, spread: 48, startVelocity: 37, origin: { x: 0.92, y: 0.75 } });
    const secondBurst = window.setTimeout(() => {
      if (reduced.matches) return;
      void fire({ ...options, particleCount: 28, spread: 100, startVelocity: 24, scalar: 0.65, origin: { x: 0.5, y: 0.42 } });
    }, 160);
    const stopForReducedMotion = () => {
      if (reduced.matches) {
        window.clearTimeout(secondBurst);
        fire.reset();
      }
    };
    reduced.addEventListener("change", stopForReducedMotion);
    return () => {
      window.clearTimeout(secondBurst);
      reduced.removeEventListener("change", stopForReducedMotion);
      fire.reset();
    };
  }, []);
  return <div className="pointer-events-none absolute inset-0 lesson-completion-overlay z-10 flex items-center justify-center bg-black/45">
    <canvas ref={canvasRef} aria-hidden="true" className="pointer-events-none absolute inset-0 z-10 h-full w-full" />
    <div className="pointer-events-auto lesson-completion-card relative z-20 w-full max-w-sm rounded-2xl bg-white text-center shadow-base">
      <div className="lesson-completion-icon mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-success-50 ring-4 ring-success-50/50 sm:mb-4 sm:size-14">
        <img src={checkIcon} alt="" className="size-7 sm:size-8" />
      </div>
      <div role="status" aria-live="polite">
        <h3 className="lesson-completion-heading text-lg font-semibold tracking-tight text-ink sm:text-xl">{t("hv.finishedTitle")}</h3>
        <p className="lesson-completion-subtitle mt-1 text-sm text-ink-regular">{title}</p>
      </div>
      {nextTitle && <p className="lesson-completion-next mt-3 text-xs text-ink-regular">{t("hv.finishedNext", { title: nextTitle })}</p>}
      <div className="lesson-completion-actions mt-5 flex justify-center gap-3">
        <button type="button" onClick={onReplay} className="rounded-md border border-line px-4 py-2 text-sm font-medium text-ink-regular hover:border-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">{t("hv.rewatch")}</button>
        <button type="button" onClick={onContinue ?? onDone} className="rounded-md bg-primary-800 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">{t(onContinue ? "hv.continueNext" : "common.done")}</button>
      </div>
    </div>
  </div>;
}
